// Active ISO 4217 currency codes. Names are resolved (and localized) at runtime
// via Intl.DisplayNames, so this list only needs the codes — matching the set of
// currencies the backend FX provider (open.er-api.com) can convert.
export const CURRENCY_CODES: string[] = [
  'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
  'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL',
  'BSD', 'BTN', 'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHF', 'CLP', 'CNY',
  'COP', 'CRC', 'CUP', 'CVE', 'CZK', 'DJF', 'DKK', 'DOP', 'DZD', 'EGP',
  'ERN', 'ETB', 'EUR', 'FJD', 'FKP', 'GBP', 'GEL', 'GHS',
  'GIP', 'GMD', 'GNF', 'GTQ', 'GYD', 'HKD', 'HNL', 'HRK', 'HTG', 'HUF',
  'IDR', 'ILS', 'INR', 'IQD', 'IRR', 'ISK', 'JMD', 'JOD',
  'JPY', 'KES', 'KGS', 'KHR', 'KMF', 'KRW', 'KWD', 'KYD', 'KZT',
  'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD', 'MAD', 'MDL', 'MGA', 'MKD',
  'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN',
  'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD', 'OMR', 'PAB', 'PEN', 'PGK',
  'PHP', 'PKR', 'PLN', 'PYG', 'QAR', 'RON', 'RSD', 'RUB', 'RWF', 'SAR',
  'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SOS', 'SRD', 'SSP',
  'STN', 'SYP', 'SZL', 'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD',
  'TWD', 'TZS', 'UAH', 'UGX', 'USD', 'UYU', 'UZS', 'VES', 'VND',
  'VUV', 'WST', 'XAF', 'XCD', 'XDR', 'XOF', 'XPF', 'YER', 'ZAR', 'ZMW',
  'ZWL',
];

const nameCache = new Map<string, Intl.DisplayNames>();

function displayNames(locale: string): Intl.DisplayNames | null {
  const key = locale || 'en';
  const cached = nameCache.get(key);
  if (cached) return cached;
  try {
    const dn = new Intl.DisplayNames([key], { type: 'currency' });
    nameCache.set(key, dn);
    return dn;
  } catch {
    return null;
  }
}

/** Localized display name for a currency code, falling back to the code itself. */
export function currencyName(code: string, locale = 'en'): string {
  const dn = displayNames(locale);
  try {
    return dn?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

export interface CurrencyOption {
  code: string;
  name: string;
}

// The currencies most travellers reach for, shown first so the dropdown doesn't
// open on obscure ones (e.g. AFN, MGA) just because they sort first by name.
const COMMON_CODES = [
  'EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CNY', 'CAD', 'AUD', 'NZD',
  'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'MXN', 'BRL', 'INR',
  'THB', 'IDR', 'SGD', 'HKD', 'KRW', 'AED', 'TRY', 'ZAR', 'MAD',
];

/** Full currency list with localized names: common ones first, then the rest
 *  alphabetically by name. */
export function currencyOptions(locale = 'en'): CurrencyOption[] {
  const toOption = (code: string): CurrencyOption => ({ code, name: currencyName(code, locale) });
  const common = COMMON_CODES.filter(c => CURRENCY_CODES.includes(c)).map(toOption);
  const rest = CURRENCY_CODES
    .filter(c => !COMMON_CODES.includes(c))
    .map(toOption)
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  return [...common, ...rest];
}

// Region (ISO 3166-1 alpha-2) → currency, for the common cases. Only used to
// pre-fill a trip's base currency from the browser locale; the user can always
// change it. Falls back to EUR when the region is unknown or has no entry.
const REGION_CURRENCY: Record<string, string> = {
  US: 'USD', GB: 'GBP', CH: 'CHF', JP: 'JPY', CN: 'CNY', CA: 'CAD', AU: 'AUD',
  NZ: 'NZD', SE: 'SEK', NO: 'NOK', DK: 'DKK', IS: 'ISK', PL: 'PLN', CZ: 'CZK',
  HU: 'HUF', RO: 'RON', BG: 'BGN', TR: 'TRY', RU: 'RUB', UA: 'UAH', MX: 'MXN',
  BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', IN: 'INR', TH: 'THB',
  ID: 'IDR', MY: 'MYR', SG: 'SGD', HK: 'HKD', KR: 'KRW', VN: 'VND', PH: 'PHP',
  AE: 'AED', SA: 'SAR', IL: 'ILS', EG: 'EGP', ZA: 'ZAR', MA: 'MAD', TW: 'TWD',
  KZ: 'KZT', UZ: 'UZS', KG: 'KGS', GE: 'GEL', RS: 'RSD',
};

/** Best-effort guess of a base currency from the browser locale. Defaults to EUR. */
export function guessCurrencyFromLocale(locale?: string): string {
  const lang = locale || (typeof navigator !== 'undefined' ? navigator.language : '') || '';
  try {
    const region = new Intl.Locale(lang).maximize().region;
    if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region];
  } catch {
    // ignore — fall through to default
  }
  return 'EUR';
}
